import { useEffect, useState, useCallback } from "react";
import { Calendar, MapPin, Clock, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getMatches } from "@/lib/api";
import { formatDateLong, formatTime } from "@/lib/dateUtils";
import { useOutletContext } from "react-router-dom";

const formatDate = (dateStr) => formatDateLong(dateStr);

const LOGO_URL = "https://customer-assets.emergentagent.com/job_kickoff-central-10/artifacts/72zh29ie_Middel%2020%40300x.png";

const teamCategories = [
  { label: "Alle Teams", value: "all" },
  { label: "Eerste ploeg", value: "EERSTE" },
  { label: "Jong FTG", value: "JONG" },
  { label: "C", value: "C" },
  { label: "U17", value: "U17" },
  { label: "U15", value: "U15" },
  { label: "U13", value: "U13" },
];

export default function Games() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;

  const fetchMatches = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setIsRefreshing(true);
    try {
      const params = {};
      if (categoryFilter !== "all") {
        params.category = categoryFilter;
      }
      const response = await getMatches(params);
      setMatches(response.data);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Error fetching matches:", error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [categoryFilter]);

  // Initial fetch
  useEffect(() => {
    fetchMatches(true);
  }, [categoryFilter, fetchMatches]);

  // Auto-refresh every 30 seconds for live scores
  useEffect(() => {
    const interval = setInterval(() => {
      fetchMatches(false);
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchMatches]);

  const filteredMatches = matches.filter((match) => {
    if (filter === "all") return true;
    return match.status === filter;
  });

  // Counts based on ALL matches, not filtered
  const upcomingCount = matches.filter((m) => m.status === "upcoming").length;
  const liveCount = matches.filter((m) => m.status === "live").length;
  const completedCount = matches.filter((m) => m.status === "completed").length;

  // Filtered lists for display
  const upcomingMatches = filteredMatches.filter((m) => m.status === "upcoming");
  const liveMatches = filteredMatches.filter((m) => m.status === "live");
  const completedMatches = filteredMatches.filter((m) => m.status === "completed");

  const MatchCard = ({ match }) => (
    <Card 
      className={`match-ticket overflow-hidden ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}
      data-testid={`match-card-${match.id}`}
    >
      <CardContent className="p-0">
        <div className="flex flex-col md:flex-row">
          {/* Date/Time Section */}
          <div className={`p-4 md:p-6 md:w-48 flex flex-col justify-center items-center border-b md:border-b-0 md:border-r ${isDark ? 'bg-slate-800/50 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
            <div className="text-center">
              <Calendar className={`h-5 w-5 mx-auto mb-2 ${isDark ? 'text-[hsl(217,91%,60%)]' : 'text-blue-600'}`} />
              <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{formatDate(match.match_date)}</p>
              <p className={`text-sm flex items-center justify-center gap-1 mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                <Clock className="h-4 w-4" />
                {match.match_time}
              </p>
            </div>
          </div>

          {/* Teams Section */}
          <div className="flex-1 p-4 md:p-6">
            <div className="flex items-center justify-between">
              {/* Home Team */}
              <div className="text-center flex-1">
                {match.home_team.includes("Futsal Gent") ? (
                  <img src={LOGO_URL} alt={match.home_team} className="h-12 md:h-16 w-auto mx-auto mb-2" />
                ) : match.home_team_logo_url ? (
                  <img src={match.home_team_logo_url} alt={match.home_team} className="h-12 md:h-16 w-auto mx-auto mb-2 object-contain" />
                ) : (
                  <div className={`h-12 md:h-16 w-12 md:w-16 mx-auto mb-2 rounded-full flex items-center justify-center ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                    <span className={`text-xl md:text-2xl font-bold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {match.home_team.charAt(0)}
                    </span>
                  </div>
                )}
                <p className={`font-bold text-sm md:text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>{match.home_team}</p>
              </div>

              {/* Score/VS */}
              <div className="text-center px-4 md:px-8 min-w-[80px] md:min-w-[120px]">
                {match.status === "completed" || match.status === "live" ? (
                  <div>
                    {match.status === "live" && (
                      <Badge className="badge-live animate-pulse-live mb-2">LIVE</Badge>
                    )}
                    <div className={`text-2xl md:text-4xl font-black font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      <span className={match.home_score > match.away_score ? "text-[hsl(48,96%,53%)]" : ""}>
                        {match.home_score}
                      </span>
                      <span className={isDark ? 'text-slate-500' : 'text-slate-400'} > - </span>
                      <span className={match.away_score > match.home_score ? "text-[hsl(48,96%,53%)]" : ""}>
                        {match.away_score}
                      </span>
                    </div>
                  </div>
                ) : (
                  <span className="text-2xl md:text-3xl font-black text-[hsl(48,96%,53%)] font-['Chivo']">VS</span>
                )}
              </div>

              {/* Away Team */}
              <div className="text-center flex-1">
                {match.away_team.includes("Futsal Gent") ? (
                  <img src={LOGO_URL} alt={match.away_team} className="h-12 md:h-16 w-auto mx-auto mb-2" />
                ) : match.away_team_logo_url ? (
                  <img src={match.away_team_logo_url} alt={match.away_team} className="h-12 md:h-16 w-auto mx-auto mb-2 object-contain" />
                ) : (
                  <div className={`h-12 md:h-16 w-12 md:w-16 mx-auto mb-2 rounded-full flex items-center justify-center ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
                    <span className={`text-xl md:text-2xl font-bold ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {match.away_team.charAt(0)}
                    </span>
                  </div>
                )}
                <p className={`font-bold text-sm md:text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>{match.away_team}</p>
              </div>
            </div>

            {/* Match Info */}
            <div className={`flex flex-wrap items-center justify-center gap-4 mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
              <Badge variant="outline" className={isDark ? 'border-white/20 text-slate-400' : 'border-slate-300 text-slate-600'}>
                {match.competition}
              </Badge>
              <span className={`text-sm flex items-center gap-1 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                <MapPin className="h-4 w-4" />
                {match.venue}
              </span>
              <Badge variant="outline" className={isDark ? 'border-white/20 text-slate-400' : 'border-slate-300 text-slate-600'}>
                {match.team_category} Team
              </Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className={`h-40 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="games-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className={`text-3xl md:text-4xl font-black font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>WEDSTRIJDEN</h1>
            <div className="flex items-center gap-3 mt-1">
              <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>Alle wedstrijden en resultaten</p>
              <button 
                onClick={() => fetchMatches(false)}
                className={`flex items-center gap-1 text-xs ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
                data-testid="refresh-scores"
              >
                <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                Bijgewerkt {formatTime(lastUpdate)}
              </button>
            </div>
          </div>
          
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className={`w-[180px] ${isDark ? 'bg-slate-800 border-white/10 text-white' : 'bg-white border-slate-300 text-slate-900'}`} data-testid="category-filter">
              <SelectValue placeholder="Selecteer team" />
            </SelectTrigger>
            <SelectContent className={isDark ? 'bg-slate-800 border-white/10' : 'bg-white border-slate-200'}>
              {teamCategories.map((cat) => (
                <SelectItem 
                  key={cat.value} 
                  value={cat.value}
                  className={isDark ? 'text-white hover:bg-slate-700' : 'text-slate-900 hover:bg-slate-100'}
                >
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Live Matches Alert */}
        {liveMatches.length > 0 && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 bg-red-500 rounded-full animate-pulse" />
              <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {liveMatches.length} Live Wedstrijd{liveMatches.length > 1 ? 'en' : ''} - Automatisch bijgewerkt elke 30s
              </span>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="all" value={filter} onValueChange={setFilter}>
          <TabsList className={`mb-8 ${isDark ? 'bg-slate-800/50 border border-white/10' : 'bg-slate-200 border border-slate-300'}`}>
            <TabsTrigger 
              value="all" 
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              data-testid="tab-all"
            >
              Alle ({matches.length})
            </TabsTrigger>
            <TabsTrigger 
              value="upcoming"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              data-testid="tab-upcoming"
            >
              Aankomend ({upcomingCount})
            </TabsTrigger>
            <TabsTrigger 
              value="live"
              className="data-[state=active]:bg-red-600 data-[state=active]:text-white"
              data-testid="tab-live"
            >
              Live ({liveCount})
            </TabsTrigger>
            <TabsTrigger 
              value="completed"
              className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              data-testid="tab-completed"
            >
              Resultaten ({completedCount})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4">
            {filteredMatches.length > 0 ? (
              filteredMatches.map((match) => <MatchCard key={match.id} match={match} />)
            ) : (
              <p className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Geen wedstrijden gevonden</p>
            )}
          </TabsContent>

          <TabsContent value="upcoming" className="space-y-4">
            {upcomingMatches.length > 0 ? (
              upcomingMatches.map((match) => <MatchCard key={match.id} match={match} />)
            ) : (
              <p className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Geen aankomende wedstrijden</p>
            )}
          </TabsContent>

          <TabsContent value="live" className="space-y-4">
            {liveMatches.length > 0 ? (
              liveMatches.map((match) => <MatchCard key={match.id} match={match} />)
            ) : (
              <p className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Geen live wedstrijden op dit moment</p>
            )}
          </TabsContent>

          <TabsContent value="completed" className="space-y-4">
            {completedMatches.length > 0 ? (
              completedMatches.map((match) => <MatchCard key={match.id} match={match} />)
            ) : (
              <p className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Geen afgelopen wedstrijden</p>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
